import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo'

// Mock data for the demo dashboard
const stats = [
  {
    label: 'Total Referrals',
    value: '0',
    iconColor: '#14b8a6',
    bgClass: 'from-teal-500/20 to-teal-500/10',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    label: 'In Trial',
    value: '0',
    iconColor: '#a78bfa',
    bgClass: 'from-violet-500/20 to-violet-500/10',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
  {
    label: 'Converted',
    value: '0',
    iconColor: '#10b981',
    bgClass: 'from-emerald-500/20 to-emerald-500/10',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
  },
  {
    label: 'Rewards Earned',
    value: 'GBP 0',
    iconColor: '#f59e0b',
    bgClass: 'from-amber-500/20 to-amber-500/10',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
        <circle cx="12" cy="12" r="9"/>
        <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="600" fill="#f59e0b" stroke="none">&#163;</text>
      </svg>
    ),
  },
]

const steps = [
  {
    number: 1,
    title: 'Share your link',
    description: 'Send your unique referral link to advisers who might benefit from WealthSpace.',
    gradient: 'from-blue-500 to-blue-600',
  },
  {
    number: 2,
    title: 'They try WealthSpace',
    description: 'Your referral gets a 7-day free trial to explore all features.',
    gradient: 'from-emerald-500 to-emerald-600',
  },
  {
    number: 3,
    title: 'They get a free month',
    description: 'When they subscribe, they receive 1 free month as a thank you.',
    gradient: 'from-amber-500 to-amber-600',
  },
  {
    number: 4,
    title: 'You earn rewards',
    description: 'Once they become a paying customer, you unlock rewards based on your tier.',
    gradient: 'from-violet-500 to-violet-600',
  },
]

const tiers = [
  {
    name: 'Starter',
    refs: '1 ref',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6"/>
        <path d="M12 2L12 12"/>
      </svg>
    ),
  },
  {
    name: 'Connector',
    refs: '3 refs',
    iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
      </svg>
    ),
  },
  {
    name: 'Advocate',
    refs: '5 refs',
    iconBg: 'bg-gradient-to-br from-violet-500 to-violet-600',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <circle cx="12" cy="12" r="7"/>
        <polyline points="12 9 12 12 13.5 13.5"/>
      </svg>
    ),
  },
  {
    name: 'Champion',
    refs: '10 refs',
    iconBg: 'bg-gradient-to-br from-amber-500 to-amber-600',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"/>
      </svg>
    ),
  },
  {
    name: 'Legend',
    refs: '25 refs',
    iconBg: 'bg-gradient-to-br from-rose-500 to-rose-600',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <path d="M12 3l1.5 4.5h4.5l-3.5 3 1.5 4.5-4-3-4 3 1.5-4.5-3.5-3h4.5z"/>
      </svg>
    ),
  },
]

const referralLink = 'https://wealthspace.io/ref/your-partner-code'

export default function PartnerDashboardPage() {
  const [copyButtonText, setCopyButtonText] = useState('Copy Link')
  const [copyButtonCopied, setCopyButtonCopied] = useState(false)

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopyButtonText('Copied!')
      setCopyButtonCopied(true)
      setTimeout(() => {
        setCopyButtonText('Copy Link')
        setCopyButtonCopied(false)
      }, 2000)
    })
  }

  const shareEmail = () => {
    const subject = encodeURIComponent('Check out WealthSpace - AI for Financial Advisers')
    const body = encodeURIComponent(`Hi,

I thought you might be interested in WealthSpace - it's AI-powered software that helps financial advisers streamline their practice.

If you sign up using my link, you'll get a 7-day free trial and a free month when you subscribe:

${referralLink}

Let me know if you have any questions!`)
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
  }

  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, '_blank')
  }

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Hey! I thought you might be interested in WealthSpace - it's great software for financial advisers. You get a free trial and a free month if you sign up here: ${referralLink}`)
    window.open(`https://wa.me/?text=${text}`, '_blank')
  }

  return (
    <>
      <SEO
        title="Partner Dashboard | WealthSpace"
        description="Track your referrals, view your progress, and claim rewards from your WealthSpace Partner Dashboard."
        url="/partner-dashboard"
      />

      <section className="py-12 md:py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          {/* Preview Mode Notice */}
          <div
            className="rounded-xl p-4 md:p-6 mb-8 flex items-center gap-4"
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05))',
              border: '1px solid rgba(245, 158, 11, 0.3)',
            }}
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(245, 158, 11, 0.2)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold mb-0.5" style={{ color: '#fbbf24' }}>Preview Mode</h4>
              <p className="text-sm text-brand-muted">
                This is a preview of your partner dashboard. Your actual dashboard will be activated within 24 hours of signing up.
              </p>
            </div>
          </div>

          {/* Dashboard Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6 mb-12">
            <div>
              <p className="text-sm text-brand-muted mb-2">Welcome back, Partner</p>
              <h1 className="font-serif text-3xl md:text-4xl font-normal text-brand-light mb-2">
                Your Partner Dashboard
              </h1>
              <p className="text-brand-subtle">Track referrals, view progress, and claim rewards</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/partner-programme"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-all"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#e8e6e1',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                Programme Details
              </Link>
              <Link
                to="/partner-signup"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-all"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#e8e6e1',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                Invite Partner
              </Link>
            </div>
          </div>

          {/* Referral Link Card */}
          <div
            className="rounded-2xl p-6 md:p-8 mb-8"
            style={{
              background: 'linear-gradient(145deg, rgba(20, 184, 166, 0.1) 0%, rgba(20, 184, 166, 0.02) 100%)',
              border: '1px solid rgba(20, 184, 166, 0.2)',
            }}
          >
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-5">
              <div>
                <h3 className="text-lg font-semibold text-brand-light mb-1">Your unique referral link</h3>
                <p className="text-sm text-brand-muted">Share this link with advisers you know - they get a free month when they subscribe!</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                readOnly
                value={referralLink}
                className="flex-1 px-5 py-4 rounded-lg text-teal-400 text-sm md:text-base"
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: '0',
                }}
              />
              <button
                onClick={copyReferralLink}
                className={`px-6 py-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all whitespace-nowrap ${
                  copyButtonCopied ? '' : 'hover:-translate-y-0.5'
                }`}
                style={{
                  background: copyButtonCopied
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #14b8a6, #10b981)',
                  color: '#1a1f2e',
                  boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
                }}
              >
                {copyButtonCopied ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                )}
                {copyButtonText}
              </button>
            </div>
            {/* Share Buttons */}
            <div className="flex flex-wrap gap-3 mt-5">
              <button
                onClick={shareEmail}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all hover:bg-white/10 hover:text-brand-light hover:border-white/20"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#a8a5a0',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Share via Email
              </button>
              <button
                onClick={shareLinkedIn}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all hover:bg-white/10 hover:text-brand-light hover:border-white/20"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#a8a5a0',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
                Share on LinkedIn
              </button>
              <button
                onClick={shareWhatsApp}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all hover:bg-white/10 hover:text-brand-light hover:border-white/20"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#a8a5a0',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
                </svg>
                Share on WhatsApp
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${stat.bgClass}`}
                >
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-brand-light mb-1">{stat.value}</div>
                <div className="text-sm text-brand-muted">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* How It Works Section */}
          <div
            className="rounded-2xl p-8 md:p-10 mb-8"
            style={{
              background: 'linear-gradient(145deg, rgba(139, 92, 246, 0.05) 0%, rgba(0,0,0,0) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.1)',
            }}
          >
            <div className="mb-8">
              <h2 className="font-serif text-2xl font-medium text-brand-light">How the referral programme works</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.number} className="text-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 text-base font-bold text-white bg-gradient-to-br ${step.gradient}`}
                  >
                    {step.number}
                  </div>
                  <h3 className="text-sm font-semibold text-brand-light mb-2">{step.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tier Progress Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Progress Card */}
            <div
              className="rounded-2xl p-6 md:p-8"
              style={{
                background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-brand-light mb-1">Your Progress</h3>
                  <p className="text-sm text-brand-muted">Track your journey to the next reward tier</p>
                </div>
                <span
                  className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.1))',
                    color: '#10b981',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  New Partner
                </span>
              </div>
              <div className="mb-6">
                <div className="flex justify-between text-sm text-brand-muted mb-2">
                  <span>0 referrals</span>
                  <span>Next: Starter (1 referral)</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: '0%',
                      background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)',
                    }}
                  />
                </div>
              </div>
              <div
                className="flex items-center gap-4 p-4 rounded-xl"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1))',
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6"/>
                    <path d="M12 2L12 12"/>
                    <path d="M17 7l-5 5-5-5"/>
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-brand-light mb-0.5">Next Reward: GBP 100 Voucher</h4>
                  <p className="text-sm text-brand-muted">Just 1 more referral to unlock the Starter tier</p>
                </div>
              </div>
            </div>

            {/* Rewards Card */}
            <div
              className="rounded-2xl p-6 md:p-8"
              style={{
                background: 'linear-gradient(145deg, rgba(245, 158, 11, 0.08) 0%, rgba(245, 158, 11, 0.02) 100%)',
                border: '1px solid rgba(245, 158, 11, 0.15)',
              }}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-brand-light mb-1">Reward Tiers</h3>
                  <p className="text-sm text-brand-muted">Unlock rewards as you refer more advisers</p>
                </div>
                <span
                  className="px-3.5 py-1.5 rounded-full text-xs font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1))',
                    color: '#a78bfa',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                  }}
                >
                  Physical Rewards
                </span>
              </div>
              {/* Rewards Summary */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div
                  className="flex-1 text-center p-5 rounded-xl"
                  style={{ background: 'rgba(0,0,0,0.2)' }}
                >
                  <div className="text-2xl font-bold mb-1" style={{ color: '#a78bfa' }}>0/5</div>
                  <div className="text-sm text-brand-muted">Tiers Unlocked</div>
                </div>
                <div
                  className="flex-1 text-center p-5 rounded-xl"
                  style={{ background: 'rgba(0,0,0,0.2)' }}
                >
                  <div className="text-2xl font-bold mb-1" style={{ color: '#14b8a6' }}>GBP 0</div>
                  <div className="text-sm text-brand-muted">Total Value Earned</div>
                </div>
              </div>
              {/* Tiers Preview */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {tiers.map((tier, i) => (
                  <div
                    key={tier.name}
                    className="text-center p-4 rounded-lg transition-all"
                    style={{
                      background: i === 0 ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.03)',
                      border: i === 0 ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                      opacity: i === 0 ? 1 : 0.5,
                    }}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${tier.iconBg}`}
                    >
                      {tier.icon}
                    </div>
                    <div
                      className="text-xs font-semibold mb-0.5"
                      style={{ color: i === 0 ? '#e8e6e1' : '#a8a5a0' }}
                    >
                      {tier.name}
                    </div>
                    <div className="text-[10px]" style={{ color: '#6b6965' }}>{tier.refs}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
